export type EnterprisePermission =
  | '*'
  | 'organization.*'
  | 'organization.view'
  | 'organization.create'
  | 'organization.update'
  | 'organization.delete'
  | 'user.*'
  | 'user.view'
  | 'user.create'
  | 'user.update'
  | 'user.disable'
  | 'team.*'
  | 'team.view'
  | 'team.create'
  | 'team.update'
  | 'team.delete'
  | 'ticket.*'
  | 'ticket.view'
  | 'ticket.create'
  | 'ticket.update'
  | 'ticket.assign'
  | 'ticket.close'
  | 'ticket.delete'
  | 'ticket.comment'
  | 'report.*'
  | 'report.view'
  | 'report.export'
  | 'automation.*'
  | 'automation.view'
  | 'automation.manage'
  | 'integration.*'
  | 'integration.view'
  | 'integration.manage'
  | 'settings.*'
  | 'settings.view'
  | 'settings.manage';

// Legacy permission mapping for backwards compatibility
export type LegacyPermission =
  | 'view_dashboard'
  | 'view_queue'
  | 'create_ticket'
  | 'edit_ticket'
  | 'assign_ticket'
  | 'resolve_ticket'
  | 'view_reports'
  | 'manage_users'
  | 'manage_teams'
  | 'manage_settings'
  | 'view_audit_logs';

export type Permission = EnterprisePermission | LegacyPermission | string;

export const ROLE_PERMISSIONS: Record<string, string[]> = {
  'Super Admin': ['*'],
  'superadmin': ['*'],
  'admin': ['*'],
  'Admin': [
    'organization.view', 'organization.create', 'organization.update',
    'user.*', 'team.*', 'ticket.*', 'report.*', 'automation.*', 'integration.*', 'settings.*',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket',
    'view_reports', 'manage_users', 'manage_teams', 'manage_settings', 'view_audit_logs'
  ],
  'Manager': [
    'team.view', 'team.update',
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'report.view',
    'view_dashboard', 'view_queue', 'view_reports'
  ],
  'Agent': [
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket'
  ],
  'agent': [
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket'
  ],
  'Technician': [
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket',
    'soc.rule_dev', 'soc.validation', 'soc.remediation'
  ],
  'technician': [
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket',
    'soc.rule_dev', 'soc.validation', 'soc.remediation'
  ],
  'Service Desk Agent': [
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket'
  ],
  'Team Lead': [
    'team.view', 'team.update',
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'report.view',
    'view_dashboard', 'view_queue', 'create_ticket', 'edit_ticket', 'assign_ticket', 'resolve_ticket', 'view_reports'
  ],
  'team_lead': [
    'team.view', 'team.update',
    'ticket.view', 'ticket.create', 'ticket.update', 'ticket.assign', 'ticket.close', 'ticket.comment',
    'report.view'
  ],
  'Viewer': [
    'ticket.view', 'report.view',
    'view_dashboard', 'view_reports'
  ],
  'Auditor': [
    'ticket.view', 'report.view', 'audit.view', 'report.export',
    'view_dashboard', 'view_reports', 'view_audit_logs'
  ],
  'SOC Analyst': [
    'ticket.view', 'ticket.update', 'report.view',
    'view_dashboard', 'view_queue', 'view_reports', 'view_audit_logs'
  ],
  'SOC Admin': [
    'ticket.view', 'ticket.create', 'ticket.update', 'report.view',
    'view_dashboard', 'view_reports', 'view_audit_logs'
  ],
  'soc admin': [
    'ticket.view', 'ticket.create', 'ticket.update', 'report.view',
    'view_dashboard', 'view_reports', 'view_audit_logs'
  ],
  'SOC Lead': [
    'ticket.view', 'ticket.create', 'ticket.update', 'report.view',
    'view_dashboard', 'view_reports', 'view_audit_logs'
  ],
  'SOC Manager': [
    'ticket.view', 'ticket.create', 'ticket.update', 'report.view',
    'view_dashboard', 'view_reports', 'view_audit_logs'
  ],
  'SOC User': [
    'soc.view', 'sentinel.view', 'ticket.view', 'report.view',
    'view_dashboard', 'view_queue', 'view_reports'
  ],
  'soc user': [
    'soc.view', 'sentinel.view', 'ticket.view', 'report.view',
    'view_dashboard', 'view_queue', 'view_reports'
  ],
  'SOC_USER': [
    'soc.view', 'sentinel.view', 'ticket.view', 'report.view',
    'view_dashboard', 'view_queue', 'view_reports'
  ],
  'SOC Technician': [
    'soc.view', 'sentinel.view', 'ticket.view', 'ticket.update',
    'view_dashboard', 'view_queue'
  ],
  'soc technician': [
    'soc.view', 'sentinel.view', 'ticket.view', 'ticket.update',
    'view_dashboard', 'view_queue'
  ],
  'SOC_TECHNICIAN': [
    'soc.view', 'sentinel.view', 'ticket.view', 'ticket.update',
    'view_dashboard', 'view_queue'
  ],
  'Department Head': [
    'department.view', 'department.manage', 'team.view', 'ticket.view', 'ticket.update',
    'report.view', 'view_dashboard', 'view_reports'
  ],
  'department head': [
    'department.view', 'department.manage', 'team.view', 'ticket.view', 'ticket.update',
    'report.view', 'view_dashboard', 'view_reports'
  ],
  'DEPARTMENT_HEAD': [
    'department.view', 'department.manage', 'team.view', 'ticket.view', 'ticket.update',
    'report.view', 'view_dashboard', 'view_reports'
  ],
  'Approver': [
    'approval.act', 'ticket.view', 'ticket.update', 'view_dashboard'
  ],
  'approver': [
    'approval.act', 'ticket.view', 'ticket.update', 'view_dashboard'
  ],
  'APPROVER': [
    'approval.act', 'ticket.view', 'ticket.update', 'view_dashboard'
  ],
  'Report Viewer': [
    'report.view', 'report.export', 'view_dashboard', 'view_reports'
  ],
  'report viewer': [
    'report.view', 'report.export', 'view_dashboard', 'view_reports'
  ],
  'REPORT_VIEWER': [
    'report.view', 'report.export', 'view_dashboard', 'view_reports'
  ],
  'User': [
    'ticket.view', 'ticket.create', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket',
    'soc.use_case_create', 'soc.use_case_view_own', 'soc.uat_signoff'
  ],
  'user': [
    'ticket.view', 'ticket.create', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket',
    'soc.use_case_create', 'soc.use_case_view_own', 'soc.uat_signoff'
  ],
  'Normal User': [
    'ticket.view', 'ticket.create', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket',
    'soc.use_case_create', 'soc.use_case_view_own', 'soc.uat_signoff'
  ],
  'normal user': [
    'ticket.view', 'ticket.create', 'ticket.comment',
    'view_dashboard', 'view_queue', 'create_ticket',
    'soc.use_case_create', 'soc.use_case_view_own', 'soc.uat_signoff'
  ]
};

// Normalizes legacy and database permission names to standard dot-notation
export function normalizePermission(perm: string): string {
  switch (perm) {
    case 'view_dashboard':
    case 'view_queue':
    case 'tickets.view_all':
    case 'tickets.view':
      return 'ticket.view';
    case 'create_ticket':
    case 'tickets.create':
      return 'ticket.create';
    case 'edit_ticket':
    case 'tickets.update_all':
    case 'tickets.update':
      return 'ticket.update';
    case 'assign_ticket':
    case 'tickets.assign':
      return 'ticket.assign';
    case 'resolve_ticket':
    case 'tickets.close':
      return 'ticket.close';
    case 'tickets.delete':
      return 'ticket.delete';
    case 'tickets.comment':
      return 'ticket.comment';
    case 'view_reports':
    case 'reports.view':
      return 'report.view';
    case 'reports.export':
      return 'report.export';
    case 'manage_users':
    case 'users.manage':
      return 'user.view';
    case 'manage_teams':
    case 'teams.manage':
      return 'team.view';
    case 'manage_settings':
      return 'settings.manage';
    case 'view_audit_logs':
    case 'audit.view':
      return 'audit.view';
    default:
      return perm;
  }
}

// Checks whether a given list of user permissions satisfies a required permission
export function evaluatePermissions(userPerms: string[], requiredPerm: string): boolean {
  if (!userPerms || userPerms.length === 0) return false;
  if (userPerms.includes('*')) return true;

  const normalizedRequired = normalizePermission(requiredPerm);
  const normalizedUserPerms = userPerms.map(normalizePermission);

  if (
    userPerms.includes(requiredPerm) ||
    userPerms.includes(normalizedRequired) ||
    normalizedUserPerms.includes(normalizedRequired)
  ) {
    return true;
  }

  // Check category wildcards (e.g., ticket.* or tickets.* satisfies ticket.view)
  const dotIndex = normalizedRequired.indexOf('.');
  if (dotIndex > 0) {
    const category = normalizedRequired.substring(0, dotIndex);
    const categoryWildcard = category + '.*';
    const pluralCategoryWildcard = category + 's.*';
    if (
      userPerms.includes(categoryWildcard) || 
      userPerms.includes(pluralCategoryWildcard) ||
      normalizedUserPerms.includes(categoryWildcard)
    ) {
      return true;
    }
  }

  return false;
}

// Check permission against either a role name or an explicit permissions list
export function hasPermission(
  roleOrPerms: string | string[] | null | undefined, 
  permission: string
): boolean {
  if (!roleOrPerms) return false;

  if (Array.isArray(roleOrPerms)) {
    if (roleOrPerms.includes('*')) return true;
    return evaluatePermissions(roleOrPerms, permission);
  }

  // It's a role string
  const role = roleOrPerms.toLowerCase().trim();
  if (role.includes('super admin') || role.includes('superadmin')) return true;

  const perms = ROLE_PERMISSIONS[roleOrPerms] || ROLE_PERMISSIONS[role] || [];
  return evaluatePermissions(perms, permission);
}

export function hasAnyPermission(
  roleOrPerms: string | string[] | null | undefined,
  permissions: string[]
): boolean {
  if (!permissions || permissions.length === 0) return true;
  return permissions.some(p => hasPermission(roleOrPerms, p));
}

export function hasAllPermissions(
  roleOrPerms: string | string[] | null | undefined,
  permissions: string[]
): boolean {
  if (!permissions || permissions.length === 0) return true;
  return permissions.every(p => hasPermission(roleOrPerms, p));
}

export function hasRole(
  currentRole: string | null | undefined,
  allowedRoles: string | string[]
): boolean {
  if (!currentRole) return false;
  const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  const cur = currentRole.toLowerCase().trim();
  return roles.some(r => r.toLowerCase().trim() === cur);
}

export function canAccessView(roleOrPerms: string | string[] | null, view: string): boolean {
  if (!roleOrPerms) return false;

  if (view === 'hub') return hasPermission(roleOrPerms, 'ticket.view');
  if (view === 'queue') return hasPermission(roleOrPerms, 'ticket.view');
  if (view.startsWith('reports')) return hasPermission(roleOrPerms, 'report.view');
  if (view === 'audit') return hasPermission(roleOrPerms, 'report.view');

  const settingsViews = [
    'settings', 'team-management', 'user-management', 'ticket-configuration',
    'automation', 'migration', 'integrations', 'email-config', 'sso-users'
  ];

  if (view.startsWith('user-management') || view === 'users') {
    return hasPermission(roleOrPerms, 'user.view');
  }

  if (view.startsWith('team-management') || view === 'teams') {
    return hasPermission(roleOrPerms, 'team.view');
  }

  if (settingsViews.some(v => view.startsWith(v))) {
    return hasPermission(roleOrPerms, 'settings.view') || hasPermission(roleOrPerms, 'settings.manage');
  }

  return false;
}

export function getDashboardRedirectForRole(role?: string | null, email?: string | null): string {
  const norm = (role || '').toLowerCase().replace(/[-_]/g, ' ').trim();
  const normEmail = (email || '').toLowerCase().trim();

  // 1. Explicit email checks for SOC admins
  if (
    normEmail.includes('achakkaravarthy') ||
    normEmail.includes('ljayaraj') ||
    normEmail.includes('hraj') ||
    normEmail.includes('pramachandran')
  ) {
    return '/admin/soc';
  }

  // 2. Explicit email checks for known Technicians
  if (normEmail.includes('rvasudevan') || normEmail.includes('akaruppasamy') || normEmail.includes('gayyasamy')) {
    return '/technician/dashboard';
  }

  // 3. SOC Roles
  if (
    norm.includes('soc') ||
    norm.includes('security') ||
    norm.includes('sentinel')
  ) {
    return '/admin/soc';
  }

  // 4. Super Admin & Admin
  if (norm.includes('super admin') || norm.includes('superadmin') || norm === 'admin') {
    return '/admin/dashboard';
  }

  // 5. Management & Leadership (Team Lead, Department Head, Manager)
  if (
    norm.includes('team lead') ||
    norm.includes('teamlead') ||
    norm.includes('department head') ||
    norm.includes('dept head') ||
    norm === 'manager'
  ) {
    return '/manager/dashboard';
  }

  // 6. Technician, Agent, Service Desk Agent
  if (
    norm === 'technician' ||
    norm.includes('technician') ||
    norm === 'agent' ||
    norm.includes('service desk') ||
    norm.includes('support engineer') ||
    norm.includes('tech')
  ) {
    return '/technician/dashboard';
  }

  // 7. Approver
  if (norm === 'approver') {
    return '/admin/automation/approvals';
  }

  // 8. Reporting & Audit
  if (
    norm.includes('report viewer') ||
    norm === 'viewer' ||
    norm === 'auditor'
  ) {
    return '/admin/reports';
  }

  // 9. Pending
  if (norm === 'pending approval' || norm === 'pending' || norm === 'invited') {
    return '/access-pending';
  }

  // 10. Default Normal User / Requester
  return '/user/dashboard';
}

